import { useCallback, useEffect, useState } from "react";
import { Box, Button, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import { DeleteOutline as DeleteIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { agentChatStyles as styles } from "./styles";

// Saved chats (COMY1-392): this role view's conversations, newest first. Open
// one to continue it; delete one or all. Deleting removes the messages and the
// files picked in them — the record of what was done (the action log) stays.

const when = (iso, locale) =>
  iso
    ? new Intl.DateTimeFormat(locale === "en" ? "en-GB" : "he-IL", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }).format(
        new Date(iso),
      )
    : "";

export default function AgentHistory({ load, locale, currentId, onOpen, onDelete }) {
  const { t } = useTranslation("agent");
  const [items, setItems] = useState(null);
  const [failed, setFailed] = useState(false);
  // the delete waiting for a second tap: a conversation id, "all", or null
  const [confirming, setConfirming] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(() => {
    setFailed(false);
    return load()
      .then(setItems)
      .catch(() => setFailed(true));
  }, [load]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const remove = async (id) => {
    if (confirming !== (id ?? "all")) return setConfirming(id ?? "all");
    setBusy(true);
    try {
      await onDelete(id);
      setConfirming(null);
      await refresh();
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  if (failed) return <Typography sx={styles.feedEmpty}>{t("history.failed")}</Typography>;
  if (!items) return <CircularProgress size={20} sx={{ color: "#46A091", alignSelf: "center", mt: "24px" }} />;
  if (!items.length) return <Typography sx={styles.feedEmpty}>{t("history.empty")}</Typography>;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      <Box component="ul" sx={styles.feed} aria-label={t("history.title")}>
        {items.map((c) => (
          <Box component="li" key={c.id} sx={styles.historyItem(c.id === currentId)}>
            <Button sx={styles.historyOpen} onClick={() => onOpen(c.id)} aria-current={c.id === currentId ? "true" : undefined}>
              <Typography component="span" sx={styles.historyTitle} dir="auto">
                {c.title}
              </Typography>
              <Typography component="span" sx={styles.feedTime}>
                {when(c.updatedAt, locale)}
              </Typography>
            </Button>
            {confirming === c.id ? (
              <Button size="small" color="error" disabled={busy} onClick={() => remove(c.id)} sx={styles.historyConfirm}>
                {t("history.confirmDelete")}
              </Button>
            ) : (
              <Tooltip title={t("history.delete")}>
                <IconButton size="small" aria-label={t("history.delete")} disabled={busy} onClick={() => remove(c.id)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ))}
      </Box>
      <Typography sx={styles.historyNote}>{t("history.keptNote")}</Typography>
      <Button
        size="small"
        color={confirming === "all" ? "error" : "inherit"}
        disabled={busy}
        onClick={() => remove(null)}
        sx={styles.historyDeleteAll}
      >
        {confirming === "all" ? t("history.confirmDeleteAll") : t("history.deleteAll")}
      </Button>
    </Box>
  );
}
